/**
 * Data layer for `campaign-archive`. Thin typed fetch wrappers over
 * `/api/v1/campaign-archive`. Raw `fetch()` is confined to this file per the
 * dash contract (D6). The BE exposes four endpoints:
 *
 *   POST   /archive/:id     Archive a campaign (created or refreshed).
 *   POST   /unarchive/:id   Remove the archival marker (Restore or Purge).
 *   GET    /list            List archived rows (requires ?include_archived=true).
 *   GET    /status/:id      Inspect a single row.
 *
 * The dash ARCHIVE page talks to `/list` + `/unarchive/:id`. "Restore" and
 * "Purge" both surface as POST `/unarchive/:id` — the UI differentiates user
 * intent via confirmation copy while the BE collapses them onto one write
 * (the marker is removed either way; campaign data itself is untouched).
 */

const BASE = "/api/v1/campaign-archive";
const TENANT_HEADER = "x-tenant-id";
const TENANT_ID = "t1";

// ---------------------------------------------------------------------------
// Types — shaped exactly like the BE's `ArchiveResponse`, plus the derived
// view fields the table shows (clicks / conversions come from a
// to-be-wired stats feed; for now they're best-effort optional fields).
// ---------------------------------------------------------------------------

export interface ArchivedCampaign {
	campaign_id: string;
	tenant_id: string;
	archived_at: number;
	archived_by: string;
	reason: string | null;
	// Optional stats — BE may extend `/list` later; we render "-" when absent.
	name?: string | null;
	clicks?: number | null;
	conversions?: number | null;
}

export interface ListArchivedResponse {
	include_archived: boolean;
	archived: ArchivedCampaign[];
}

export interface UnarchiveResponse {
	unarchived: boolean;
	campaign_id: string;
}

// ---------------------------------------------------------------------------
// Parse helpers
// ---------------------------------------------------------------------------

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function headers(extra: Record<string, string> = {}): HeadersInit {
	return {
		accept: "application/json",
		[TENANT_HEADER]: TENANT_ID,
		...extra,
	};
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * Fetch archived campaigns for the current tenant. The BE's /list endpoint
 * requires ?include_archived=true to surface archived rows — without it you
 * get an empty list. The dash Archive page only ever wants archived rows, so
 * we hard-code the flag.
 */
export async function listArchived(): Promise<ListArchivedResponse> {
	const res = await fetch(`${BASE}/list?include_archived=true`, {
		method: "GET",
		credentials: "include",
		headers: headers(),
	});
	return parseOrThrow<ListArchivedResponse>(res);
}

/**
 * Restore — removes the archive marker so the campaign re-appears in the
 * default (non-archived) campaign list.
 */
export async function restoreCampaign(id: string): Promise<UnarchiveResponse> {
	const res = await fetch(`${BASE}/unarchive/${encodeURIComponent(id)}`, {
		method: "POST",
		credentials: "include",
		headers: headers({ "content-type": "application/json" }),
	});
	return parseOrThrow<UnarchiveResponse>(res);
}

/**
 * Purge — removes the archive marker permanently. On this BE, purge and
 * restore resolve to the same write (DELETE from the archive table); the
 * dash layer differentiates intent in the confirmation copy, not on the
 * wire. If the BE later gains a dedicated `/purge/:id` with additional
 * side-effects (e.g. cascading delete of stats), swap the target here —
 * the hook boundary stays stable.
 */
export async function purgeCampaign(id: string): Promise<UnarchiveResponse> {
	const res = await fetch(`${BASE}/unarchive/${encodeURIComponent(id)}`, {
		method: "POST",
		credentials: "include",
		headers: headers({ "content-type": "application/json" }),
	});
	return parseOrThrow<UnarchiveResponse>(res);
}

// ---------------------------------------------------------------------------
// Query keys — canonical shape used by hooks + tests.
// ---------------------------------------------------------------------------

export const campaignArchiveQueryKeys = {
	all: ["campaign-archive"] as const,
	list: () => [...campaignArchiveQueryKeys.all] as const,
	detail: (id: string) => [...campaignArchiveQueryKeys.all, id] as const,
};
